import { useState } from 'react';
import { Button } from '@rsos/base-components';
import { DebugPanel } from '@rsos/debug-panel';
import { useCurrentOrgName } from '@rsos/sinatra/src/selectors';
import {
  useDeleteInteragencyConversationMutation,
  useListInteragencyConversationsQuery,
} from '../../reduxDataStore/interagency/interagencyAPI';
import styles from './InteragencyDebugPanel.module.css';

interface InteragencyDebugPanelProps {
  onClose?: () => void;
}

export const InteragencyDebugPanel = ({
  onClose,
}: InteragencyDebugPanelProps) => {
  const orgName = useCurrentOrgName();
  const { data, isFetching } = useListInteragencyConversationsQuery(
    { orgName: orgName ?? '' },
    { skip: !orgName },
  );

  const [
    deleteConversation,
    { isLoading: isDeleting, isSuccess, error, reset },
  ] = useDeleteInteragencyConversationMutation();

  const [selectedId, setSelectedId] = useState('');

  const conversations = data?.conversations ?? [];
  const hasError = !!error;

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedId(e.target.value);
    reset();
  };

  const handleDelete = async () => {
    if (!selectedId || !orgName) return;
    const result = await deleteConversation({
      orgName,
      conversationId: selectedId,
    });
    if (!('error' in result)) {
      setSelectedId('');
    }
  };

  return (
    <DebugPanel title="Interagency Chat Panel" onClose={onClose}>
      <p className={styles.description}>
        Delete an interagency conversation. All session rows for the selected
        conversation will be deactivated.
      </p>
      <div className={styles.row}>
        <select
          className={styles.select}
          value={selectedId}
          onChange={handleSelectChange}
          disabled={isFetching || conversations.length === 0}
        >
          <option value="">
            {isFetching
              ? 'Loading…'
              : conversations.length === 0
                ? 'No conversations'
                : 'Select a conversation'}
          </option>
          {conversations.map(c => (
            <option key={c.conversation_id} value={c.conversation_id}>
              {c.conversation_name} ({c.conversation_id})
            </option>
          ))}
        </select>
        <Button
          btnType="warning_primary"
          onClick={handleDelete}
          disabled={!selectedId || isDeleting}
        >
          Delete
        </Button>
      </div>
      {isSuccess && (
        <p className={styles.success}>Conversation deleted successfully.</p>
      )}
      {hasError && (
        <p className={styles.error}>Failed to delete conversation.</p>
      )}
    </DebugPanel>
  );
};
